<template>
  <!-- 供货商门户-订单发货管理:独立于管理后台,一套代码PC(>768px表格)/手机(<=768px卡片)自适应 -->
  <div class="supplier-orders">
    <!-- 顶栏:供货商名 + 退出登录 -->
    <header class="so-header">
      <div class="so-header__brand"><i class="el-icon-shopping-bag-1"></i> 权益商城·供货商平台</div>
      <div class="so-header__right">
        <span class="so-header__name">{{ supplierName }}</span>
        <el-button type="danger" plain size="small" @click="handleLogout">退出登录</el-button>
      </div>
    </header>

    <!-- 筛选栏:状态tab(全部/待发货/已发货/已完成) -->
    <div class="so-filter">
      <el-radio-group v-model="statusFilter" size="small" @change="handleFilterChange">
        <el-radio-button label="">全部</el-radio-button>
        <el-radio-button :label="1">待发货</el-radio-button>
        <el-radio-button :label="2">已发货</el-radio-button>
        <el-radio-button :label="3">已完成</el-radio-button>
      </el-radio-group>
      <div class="so-filter__ops">
        <el-button size="small" icon="el-icon-refresh" @click="refreshList">刷新</el-button>
        <el-button size="small" type="warning" icon="el-icon-download" :loading="exportLoading"
          @click="handleExport">导出</el-button>
        <el-button size="small" type="success" icon="el-icon-upload2" @click="openImport">批量发货导入</el-button>
      </div>
    </div>

    <!-- 列表滚动区:移动端在顶部下拉刷新(touch事件PC端不触发,互不影响) -->
    <div ref="scrollWrap" class="so-body" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd">
      <!-- 下拉刷新指示器:随手指移动展开 -->
      <div v-show="pullDistance > 0" class="so-pull" :style="{ height: pullDistance + 'px' }">
        <i :class="pullRefreshing ? 'el-icon-loading' : 'el-icon-arrow-down'"></i>
        <span>{{ pullTip }}</span>
      </div>

      <!-- 错误态:后端业务失败/网络异常时展示,可重试 -->
      <div v-if="errorMsg && !listLoading" class="so-state">
        <i class="el-icon-warning-outline"></i>
        <p>{{ errorMsg }}</p>
        <el-button type="primary" plain size="small" @click="getList()">重试</el-button>
      </div>

      <template v-else>
        <!-- PC端表格布局(>768px,移动端由CSS隐藏) -->
        <div v-loading="listLoading" class="so-table" element-loading-text="数据加载中...">
          <el-table :data="tableData" border size="mini" style="width: 100%">
            <el-table-column prop="amoOrderNo" label="订单号" min-width="200" show-overflow-tooltip></el-table-column>
            <el-table-column label="商品" min-width="150" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.amgName || '-' }} ×{{ row.amoNum || 1 }}</template>
            </el-table-column>
            <el-table-column label="单价" width="90" align="center">
              <template slot-scope="{ row }">{{ row.amoPriceStr || '-' }}</template>
            </el-table-column>
            <el-table-column label="收货人" min-width="80">
              <template slot-scope="{ row }">{{ row.amaReceiver || '-' }}</template>
            </el-table-column>
            <el-table-column label="收货电话" min-width="120">
              <template slot-scope="{ row }">{{ row.amaPhone || '-' }}</template>
            </el-table-column>
            <el-table-column label="收货地址" min-width="200" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.amaRegion || '' }}{{ row.amaDetail || '' }}</template>
            </el-table-column>
            <el-table-column label="下单时间" min-width="150">
              <template slot-scope="{ row }">{{ row.amoPayTime || '-' }}</template>
            </el-table-column>
            <el-table-column label="状态" min-width="140">
              <template slot-scope="{ row }">
                <el-tag :type="statusTagType(row.amoStatus)" size="mini">{{ row.amoStatusDes || '-' }}</el-tag>
                <!-- 退款申请中的订单:后端下发暂缓发货标注,发货会被后端拦截 -->
                <el-tooltip v-if="row.amoRefundStatusDes" :content="row.amoRefundStatusDes" placement="top">
                  <el-tag type="danger" size="mini" class="so-hold-tag">退款申请中</el-tag>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="物流信息" min-width="190" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span v-if="row.amoStatus >= 2">{{ row.amoExpressCompanyName }} {{ row.amoExpressNo }}</span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" fixed="right" width="120" align="center">
              <template slot-scope="{ row }">
                <el-button v-if="row.amoStatus === 1" type="primary" size="mini" @click="openShip(row)">发货
                </el-button>
                <el-button v-else-if="row.amoStatus === 2" type="warning" plain size="mini" @click="openShip(row)">
                  修改单号</el-button>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 手机端卡片布局(<=768px,PC端由CSS隐藏):每单一卡 -->
        <div v-loading="listLoading" class="so-cards" element-loading-text="数据加载中...">
          <div v-for="row in tableData" :key="row.amoId" class="so-card">
            <div class="so-card__head">
              <el-tag :type="statusTagType(row.amoStatus)" size="small">{{ row.amoStatusDes || '-' }}</el-tag>
              <span class="so-card__time">{{ row.amoPayTime || '' }}</span>
            </div>
            <!-- 暂缓发货警示条(退款申请中) -->
            <div v-if="row.amoRefundStatusDes" class="so-card__hold">
              <i class="el-icon-warning-outline"></i>{{ row.amoRefundStatusDes }}
            </div>
            <div class="so-card__goods">{{ row.amgName || '-' }} ×{{ row.amoNum || 1 }}</div>
            <div class="so-card__row">
              <span class="so-card__label">单价</span>
              <span class="so-card__value">{{ row.amoPriceStr || '-' }}</span>
            </div>
            <div class="so-card__row">
              <span class="so-card__label">订单号</span>
              <span class="so-card__value">{{ row.amoOrderNo || '-' }}</span>
            </div>
            <div class="so-card__row">
              <span class="so-card__label">收货人</span>
              <span class="so-card__value">{{ row.amaReceiver || '-' }} {{ row.amaPhone || '' }}</span>
            </div>
            <div class="so-card__row">
              <span class="so-card__label">地址</span>
              <span class="so-card__value">{{ row.amaRegion || '' }}{{ row.amaDetail || '' }}</span>
            </div>
            <div v-if="row.amoStatus >= 2" class="so-card__row">
              <span class="so-card__label">物流</span>
              <span class="so-card__value">{{ row.amoExpressCompanyName }} {{ row.amoExpressNo }}</span>
            </div>
            <div class="so-card__ops">
              <el-button v-if="row.amoStatus === 1" type="primary" @click="openShip(row)">发货</el-button>
              <el-button v-else-if="row.amoStatus === 2" type="warning" plain @click="openShip(row)">修改单号
              </el-button>
            </div>
          </div>
        </div>

        <!-- 空态 -->
        <el-empty v-if="!listLoading && tableData.length === 0" description="暂无订单"></el-empty>
      </template>

      <!-- 分页:移动端精简布局(prev/pager/next),PC含总数;错误态下隐藏 -->
      <div v-if="!errorMsg" class="so-pagination">
        <el-pagination background :layout="pageLayout" :page-size="pageSize" :total="total"
          :current-page="currentPage" @current-change="handlePageChange"></el-pagination>
      </div>
    </div>

    <!-- 发货/修改单号弹窗:快递公司接口下拉(不可手填)+运单号>=8位+单号自动识别 -->
    <el-dialog :title="shipRow && shipRow.amoStatus === 1 ? '订单发货' : '修改快递单号'" :visible.sync="shipDialog"
      width="480px" custom-class="supplier-ship-dialog" :close-on-click-modal="false" @closed="handleShipClosed">
      <div v-if="shipRow" class="so-ship__info">
        <p><span>订单号：</span>{{ shipRow.amoOrderNo }}</p>
        <p><span>收货人：</span>{{ shipRow.amaReceiver || '-' }} {{ shipRow.amaPhone || '' }}</p>
        <p><span>收货地址：</span>{{ shipRow.amaRegion || '' }}{{ shipRow.amaDetail || '' }}</p>
      </div>
      <!-- 退款申请中:醒目提示暂缓发货(后端也会拦截兜底) -->
      <el-alert v-if="shipRow && shipRow.amoRefundStatusDes" :title="shipRow.amoRefundStatusDes" type="error"
        :closable="false" show-icon class="so-ship__alert"></el-alert>
      <el-alert v-else-if="shipRow && shipRow.amoStatus === 1" title="录入运单号后订单将转为已发货" type="warning"
        :closable="false" show-icon class="so-ship__alert"></el-alert>
      <el-form ref="shipForm" :model="shipForm" :rules="shipRules" label-width="90px" @submit.native.prevent>
        <el-form-item label="快递公司" prop="expressCompany">
          <el-select v-model="shipForm.expressCompany" filterable placeholder="请选择快递公司" style="width: 100%">
            <el-option v-for="item in expressCompanies" :key="item.comCode" :label="item.name"
              :value="item.comCode"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="运单号" prop="expressNo">
          <el-input v-model.trim="shipForm.expressNo" maxlength="50" placeholder="请输入运单号(至少8位)">
            <el-button slot="append" :loading="identifyLoading" @click="identifyCompany">自动识别</el-button>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="shipDialog = false">取 消</el-button>
        <el-button type="primary" :loading="shipSubmitLoading" @click="submitShip">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 批量发货导入弹窗:导出的excel填"快递公司/运单号"两列后回传,逐行返回成功/失败原因 -->
    <el-dialog title="批量发货导入" :visible.sync="importDialog" width="680px" custom-class="supplier-import-dialog"
      :close-on-click-modal="false" @closed="handleImportClosed">
      <el-alert type="info" :closable="false" show-icon class="so-import__tip"
        title="先用「导出」下载excel作模板:在「快递公司」「运单号」两列填好后上传,快递公司填名称或编码均可,表头文字勿改动;单次最多1000条"></el-alert>
      <div class="so-import__ops">
        <el-upload action="#" accept=".xlsx,.xls" :auto-upload="false" :show-file-list="false"
          :disabled="importLoading" :on-change="handleImportFile">
          <el-button type="primary" size="small" icon="el-icon-upload2" :loading="importLoading">
            {{ importLoading ? "导入中..." : "选择excel文件并导入" }}</el-button>
        </el-upload>
      </div>
      <div v-if="importResult" class="so-import__result">
        <p class="so-import__summary">
          共 {{ importResult.total }} 条:成功 <span class="is-ok">{{ importResult.successCount }}</span> 条,失败
          <span class="is-bad">{{ importResult.failCount }}</span> 条
        </p>
        <el-table v-if="(importResult.rows || []).length" :data="importResult.rows" border size="mini" max-height="320">
          <el-table-column prop="orderNo" label="订单号" min-width="190" show-overflow-tooltip></el-table-column>
          <el-table-column prop="expressNo" label="运单号" min-width="130" show-overflow-tooltip></el-table-column>
          <el-table-column label="结果" width="80" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="row.success ? 'success' : 'danger'" size="mini">{{ row.success ? "成功" : "失败" }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="失败原因" min-width="170" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.msg || '-' }}</template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="importDialog = false">关 闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMallOrderListSupplier,
  shipMallOrderSupplier,
  getExpressCompanyListSupplier,
  getExpressCompanyByNoSupplier,
  exportMallOrderExcelSupplier,
  importMallOrderShipSupplier,
} from "../../api/api";

// 下拉刷新:触发阈值与最大跟手高度(px),带0.5阻尼
const PULL_THRESHOLD = 70;
const PULL_MAX = 100;
// 响应式断点:<=768px按手机卡片式布局处理
const MOBILE_QUERY = "(max-width: 768px)";

export default {
  name: "SupplierOrders",
  data() {
    return {
      supplierName: "",
      isMobile: false,
      mediaQuery: null,
      // 列表状态
      statusFilter: "",
      currentPage: 1,
      pageSize: 10,
      total: 0,
      tableData: [],
      listLoading: false,
      errorMsg: "",
      // 快递公司下拉(接口拉取,编码强校验,不允许手填)
      expressCompanies: [],
      // 发货弹窗
      shipDialog: false,
      shipSubmitLoading: false,
      identifyLoading: false,
      shipRow: null,
      shipForm: {
        amoId: null,
        expressCompany: "",
        expressNo: "",
      },
      shipRules: {
        expressCompany: [{ required: true, message: "请选择快递公司", trigger: "change" }],
        expressNo: [
          { required: true, message: "请输入运单号", trigger: "blur" },
          // 与后端校验口径一致:去空格后至少8位
          {
            validator: (rule, value, callback) =>
              value && value.trim().length >= 8 ? callback() : callback(new Error("运单号至少8位")),
            trigger: "blur",
          },
        ],
      },
      // 下拉刷新状态(touch事件驱动,PC端不触发)
      touchStartY: 0,
      touchPulling: false,
      pullDistance: 0,
      pullRefreshing: false,
      // 导出(当前状态筛选同口径下载excel,兼批量发货模板)
      exportLoading: false,
      // 批量发货导入
      importDialog: false,
      importLoading: false,
      importResult: null,
      // keep-alive账号切换检测:记录当前列表数据对应的token,token变化(换账号重登)时重拉
      listToken: "",
    };
  },
  computed: {
    // 移动端分页只保留翻页核心,PC展示总数
    pageLayout() {
      return this.isMobile ? "prev, pager, next" : "total, prev, pager, next";
    },
    pullTip() {
      if (this.pullRefreshing) {
        return "正在刷新...";
      }
      return this.pullDistance >= PULL_THRESHOLD ? "释放立即刷新" : "下拉刷新";
    },
  },
  created() {
    document.title = "供货商订单管理 - 权益商城";
    this.supplierName = sessionStorage.getItem("supplierName") || "供货商";
    if (!sessionStorage.getItem("supplierToken")) {
      // 页面级登录态校验(路由守卫已放行/supplier前缀,登录态由本页面自行判断)
      this.$router.replace("/supplier/login").catch(() => {}); // catch:同地址重复导航的console噪音
    }
  },
  mounted() {
    // 响应式断点监听:跨过768px时切换分页布局等交互形态
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.isMobile = this.mediaQuery.matches;
    this.mediaQuery.addListener(this.handleMediaChange);
  },
  activated() {
    // 顶层router-view被keep-alive缓存:每次激活都校验登录态;
    // token变化说明换了账号/重新登录,重置筛选并重拉列表,避免展示上一账号数据
    document.title = "供货商订单管理 - 权益商城";
    const token = sessionStorage.getItem("supplierToken");
    if (!token) {
      this.$router.replace("/supplier/login").catch(() => {}); // catch:同地址重复导航的console噪音
      return;
    }
    if (token !== this.listToken) {
      this.listToken = token;
      this.statusFilter = "";
      this.currentPage = 1;
      this.getList();
    }
    // 快递公司列表与账号无关,缓存一次即可
    if (this.expressCompanies.length === 0) {
      this.loadCompanies();
    }
  },
  beforeDestroy() {
    // 清理断点监听,防内存泄漏
    if (this.mediaQuery) {
      this.mediaQuery.removeListener(this.handleMediaChange);
    }
  },
  methods: {
    handleMediaChange(e) {
      this.isMobile = e.matches;
    },
    // 订单状态胶囊颜色(1待发货warning 2已发货primary 3已完成success)
    statusTagType(status) {
      const map = { 1: "warning", 2: "", 3: "success" };
      return map[status] || "info";
    },
    // 业务失败统一处理:登录信息失效时清token回登录页,返回true表示已跳转
    handleBizError(res) {
      const msg = (res && res.msg) || "";
      if (msg.indexOf("登录信息失效") !== -1) {
        this.clearSupplierSession();
        this.$message.error(msg);
        this.$router.replace("/supplier/login").catch(() => {}); // catch:同地址重复导航的console噪音
        return true;
      }
      return false;
    },
    clearSupplierSession() {
      sessionStorage.removeItem("supplierToken");
      sessionStorage.removeItem("supplierName");
    },
    // 拉取订单列表(返回Promise供下拉刷新等待完成)
    getList() {
      this.listLoading = true;
      this.errorMsg = "";
      const params = { currentPage: this.currentPage, pageSize: this.pageSize };
      if (this.statusFilter !== "") {
        params.amoStatus = this.statusFilter;
      }
      return getMallOrderListSupplier(params).then((res) => {
        this.listLoading = false;
        if (!res || res.status !== 200) {
          if (this.handleBizError(res)) {
            return;
          }
          this.errorMsg = (res && res.msg) || "加载失败,请稍后重试";
          return;
        }
        this.tableData = res.data || [];
        this.total = res.total || 0;
      }).catch(() => {
        this.listLoading = false;
        this.errorMsg = "网络异常,请稍后重试";
      });
    },
    // 快递公司下拉数据
    loadCompanies() {
      getExpressCompanyListSupplier().then((res) => {
        if (!res || res.status !== 200) {
          this.handleBizError(res);
          return;
        }
        this.expressCompanies = res.data || [];
      });
    },
    handleFilterChange() {
      this.currentPage = 1;
      this.getList();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.getList();
    },
    refreshList() {
      this.currentPage = 1;
      this.getList();
    },
    // ============ 下拉刷新(touch事件仅移动端触发) ============
    onTouchStart(e) {
      if (this.pullRefreshing) {
        return;
      }
      // 仅当列表滚动在顶部时进入下拉判定
      const wrap = this.$refs.scrollWrap;
      this.touchPulling = !!wrap && wrap.scrollTop <= 0;
      this.touchStartY = e.touches[0].clientY;
    },
    onTouchMove(e) {
      if (!this.touchPulling || this.pullRefreshing) {
        return;
      }
      const delta = e.touches[0].clientY - this.touchStartY;
      if (delta <= 0) {
        this.pullDistance = 0;
        return;
      }
      // 顶部下拉时阻止原生滚动,带阻尼跟手展开指示器
      e.preventDefault();
      this.pullDistance = Math.min(Math.round(delta * 0.5), PULL_MAX);
    },
    onTouchEnd() {
      if (!this.touchPulling || this.pullRefreshing) {
        return;
      }
      this.touchPulling = false;
      if (this.pullDistance >= PULL_THRESHOLD) {
        this.doPullRefresh();
      } else {
        this.pullDistance = 0;
      }
    },
    async doPullRefresh() {
      this.pullRefreshing = true;
      this.pullDistance = 44;
      this.currentPage = 1;
      try {
        await this.getList();
      } catch (e) {
        // getList内部已兜底,这里只为防未处理rejection
      }
      // 稍作停留再收起指示器,避免闪烁
      setTimeout(() => {
        this.pullRefreshing = false;
        this.pullDistance = 0;
      }, 300);
    },
    // ============ 发货/修改单号 ============
    openShip(row) {
      this.shipRow = row;
      this.shipDialog = true;
      this.$nextTick(() => {
        this.shipForm = {
          amoId: row.amoId,
          // 回显已有快递公司编码(不在接口下拉列表中时置空重选,后端按编码强校验)
          expressCompany: this.expressCompanies.some((item) => item.comCode === row.amoExpressCompany)
            ? row.amoExpressCompany
            : "",
          expressNo: row.amoExpressNo || "",
        };
        this.$refs.shipForm.clearValidate();
      });
    },
    handleShipClosed() {
      this.$refs.shipForm.resetFields();
      this.shipForm = { amoId: null, expressCompany: "", expressNo: "" };
      this.shipRow = null;
    },
    // 单号自动识别快递公司:成功回填下拉选中,失败提示手选
    identifyCompany() {
      const expressNo = (this.shipForm.expressNo || "").trim();
      if (expressNo.length < 8) {
        this.$message.warning("请先填写至少8位的运单号");
        return;
      }
      this.identifyLoading = true;
      getExpressCompanyByNoSupplier({ expressNo }).then((res) => {
        this.identifyLoading = false;
        if (!res || res.status !== 200) {
          if (!this.handleBizError(res)) {
            this.$message.warning((res && res.msg) || "识别失败,请从下拉列表选择快递公司");
          }
          return;
        }
        const list = res.data || [];
        if (list.length === 0) {
          this.$message.warning("未识别到支持的快递公司,请手动选择");
          return;
        }
        this.shipForm.expressCompany = list[0].comCode;
        this.$message.success(
          list.length > 1 ? `识别到${list.length}家,已选择:${list[0].name},可手动切换` : `已识别:${list[0].name}`
        );
      }).catch(() => {
        this.identifyLoading = false;
      });
    },
    // 提交发货:后端校验编码强校验/待发货转已发货/已发货重录纠错/退款中拦截
    submitShip() {
      this.$refs.shipForm.validate((valid) => {
        if (!valid) {
          return;
        }
        this.shipSubmitLoading = true;
        shipMallOrderSupplier(this.shipForm).then((res) => {
          this.shipSubmitLoading = false;
          if (!res || res.status !== 200) {
            if (!this.handleBizError(res)) {
              this.$message.error((res && res.msg) || "提交失败,请稍后重试");
            }
            return;
          }
          this.$message.success(this.shipRow && this.shipRow.amoStatus === 1 ? "发货成功" : "单号已更新");
          this.shipDialog = false;
          // 刷新列表:当前页只剩本条且非第一页时回退一页,避免刷新出空页
          if (this.tableData.length === 1 && this.currentPage > 1) {
            this.currentPage -= 1;
          }
          this.getList();
        }).catch(() => {
          this.shipSubmitLoading = false;
        });
      });
    },
    // 退出登录:清供货商会话回登录页(不动管理后台登录态)
    handleLogout() {
      this.$confirm("确认退出供货商登录?", "提示", { type: "warning" })
        .then(() => {
          this.clearSupplierSession();
          this.$router.replace("/supplier/login").catch(() => {}); // catch:同地址重复导航的console噪音
        })
        .catch(() => {});
    },
    // ============ 导出/批量发货导入 ============
    // 导出excel(与当前状态筛选同口径;裸axios返回arraybuffer,按xlsx魔数判别业务错误,同PC商城订单页口径)
    handleExport() {
      this.exportLoading = true;
      const params = this.statusFilter === "" ? {} : { amoStatus: this.statusFilter };
      exportMallOrderExcelSupplier(params).then((res) => {
        this.exportLoading = false;
        if (!res || !res.data) {
          this.$message.error("导出失败");
          return;
        }
        const buf = res.data;
        // responseType=arraybuffer时后端业务错误返回的JSON也会进then:
        // 以xlsx魔数(PK=0x50 0x4B,zip头)判别,非PK按文本解析取msg提示,不下载坏文件
        const head = new Uint8Array(buf.slice(0, 2));
        if (!(head[0] === 0x50 && head[1] === 0x4B)) {
          let msg = "导出失败";
          try {
            const json = JSON.parse(new TextDecoder("utf-8").decode(buf));
            if (json && json.msg) {
              msg = json.msg;
            }
          } catch (e) {
            // 响应体非JSON(如网关HTML错误页)时保持默认提示
          }
          if (msg.indexOf("登录信息失效") !== -1) {
            this.handleBizError({ msg });
            return;
          }
          this.$message.error(msg);
          return;
        }
        const blob = new Blob([buf], { type: "application/vnd.ms-excel" });
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.setAttribute("href", objectUrl);
        a.setAttribute("download", "商城订单.xlsx");
        a.click();
        URL.revokeObjectURL(objectUrl);
        this.$message.success("导出成功");
      }).catch(() => {
        this.exportLoading = false;
        this.$message.error("导出失败");
      });
    },
    openImport() {
      this.importResult = null;
      this.importDialog = true;
    },
    // 选择文件即上传(el-upload自动上传关闭):FormData带file,后端逐行发货并返回结果
    handleImportFile(file) {
      if (this.importLoading) {
        return;
      }
      if (!file || !file.raw || !/\.xlsx?$/i.test(file.name || "")) {
        this.$message.warning("请选择excel文件(.xlsx/.xls)");
        return;
      }
      const formData = new FormData();
      formData.append("file", file.raw);
      this.importLoading = true;
      importMallOrderShipSupplier(formData).then((res) => {
        this.importLoading = false;
        if (!res || res.status !== 200) {
          if (!this.handleBizError(res)) {
            this.$message.error((res && res.msg) || "导入失败,请稍后重试");
          }
          return;
        }
        this.importResult = res.data || {};
        const tip = this.importResult.failCount > 0
          ? `导入完成:成功${this.importResult.successCount}条,失败${this.importResult.failCount}条,详见下方明细`
          : `全部发货成功:${this.importResult.successCount}条`;
        this.$message[this.importResult.failCount > 0 ? "warning" : "success"](tip);
      }).catch(() => {
        this.importLoading = false;
      });
    },
    // 关闭导入弹窗:清结果并刷新列表(部分订单可能已发货)
    handleImportClosed() {
      this.importResult = null;
      this.refreshList();
    },
  },
};
</script>

<style scoped>
.supplier-orders {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f0f3f8;
  overflow: hidden;
}

/* 顶栏 */
.so-header {
  flex-shrink: 0;
  height: 56px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  box-shadow: 0 1px 6px rgba(31, 45, 61, 0.08);
  z-index: 5;
}

.so-header__brand {
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
}

.so-header__brand i {
  color: #3a7afe;
  margin-right: 4px;
}

.so-header__right {
  display: flex;
  align-items: center;
}

.so-header__name {
  margin-right: 12px;
  font-size: 14px;
  color: #5a6a85;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 筛选栏 */
.so-filter {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
}

/* 筛选栏右侧操作组 */
.so-filter__ops {
  display: flex;
  align-items: center;
}

.so-filter__ops .el-button + .el-button {
  margin-left: 8px;
}

/* 批量发货导入弹窗 */
.so-import__tip {
  margin-bottom: 14px;
}

.so-import__ops {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.so-import__summary {
  margin: 0 0 10px;
  font-size: 14px;
  color: #3b4757;
}

.so-import__summary .is-ok {
  color: #67c23a;
  font-weight: 600;
}

.so-import__summary .is-bad {
  color: #f56c6c;
  font-weight: 600;
}

/* 列表滚动区 */
.so-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 16px;
  -webkit-overflow-scrolling: touch;
}

/* 下拉刷新指示器 */
.so-pull {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #8a97a8;
}

.so-pull i {
  font-size: 16px;
}

/* 错误态 */
.so-state {
  background: #fff;
  border-radius: 8px;
  padding: 48px 16px;
  text-align: center;
  color: #8a97a8;
}

.so-state i {
  font-size: 40px;
  color: #f7b500;
}

.so-state p {
  margin: 12px 0 16px;
  font-size: 14px;
}

/* PC表格容器 */
.so-table {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 6px rgba(31, 45, 61, 0.06);
}

.so-hold-tag {
  margin-left: 4px;
}

/* 移动端卡片容器:PC端隐藏 */
.so-cards {
  display: none;
}

.so-card {
  background: #fff;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 1px 6px rgba(31, 45, 61, 0.06);
}

.so-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.so-card__time {
  font-size: 11px;
  color: #a8b3c1;
}

.so-card__hold {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fef0f0;
  color: #f56c6c;
  font-size: 12px;
  line-height: 1.5;
}

.so-card__hold i {
  margin-right: 4px;
}

.so-card__goods {
  margin-top: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
  line-height: 1.5;
}

.so-card__row {
  display: flex;
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.5;
}

.so-card__label {
  flex-shrink: 0;
  width: 48px;
  color: #8a97a8;
}

.so-card__value {
  flex: 1;
  color: #3b4757;
  word-break: break-all;
}

.so-card__ops {
  margin-top: 12px;
}

.so-card__ops .el-button {
  width: 100%;
}

/* 分页 */
.so-pagination {
  padding: 14px 0 4px;
  text-align: center;
}

/* 发货弹窗内的订单摘要 */
.so-ship__info p {
  font-size: 13px;
  color: #5a6a85;
  line-height: 1.8;
}

.so-ship__info p span {
  color: #8a97a8;
}

.so-ship__alert {
  margin-bottom: 14px;
}

/* ============ 响应式断点:<=768px切换手机卡片式布局 ============ */
@media (max-width: 768px) {
  .so-header {
    height: 50px;
    padding: 0 12px;
  }

  .so-header__brand {
    font-size: 14px;
  }

  .so-header__name {
    max-width: 96px;
    font-size: 13px;
    margin-right: 8px;
  }

  .so-filter {
    padding: 8px 12px;
    flex-wrap: wrap;
    row-gap: 8px;
  }

  /* 触控区放大:筛选按钮42px高 */
  .so-filter /deep/ .el-radio-button__inner {
    padding: 11px 14px;
    font-size: 14px;
  }

  .so-body {
    padding: 0 12px 12px;
  }

  /* 切换为卡片布局 */
  .so-table {
    display: none;
  }

  .so-cards {
    display: block;
  }

  /* 触控区放大:操作按钮加高加大字号 */
  .so-card__ops /deep/ .el-button {
    height: 42px;
    font-size: 15px;
  }

  /* 弹窗接近全屏 */
  .supplier-orders /deep/ .supplier-ship-dialog {
    width: 92% !important;
    margin-top: 8vh !important;
  }

  .supplier-orders /deep/ .supplier-ship-dialog .el-input__inner,
  .supplier-orders /deep/ .supplier-ship-dialog .el-input-group__append .el-button {
    height: 42px;
    font-size: 14px;
  }
}
</style>
